<template>
  <div>
    <div class="mx-auto px-4 py-4 sm:px-6 sm:py-6 lg:mt-12">
      <h2 class="sr-only">Checkout</h2>
      <ValidationObserver ref="observer" v-slot="{ handleSubmit } ">
        <form class="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16">
          <div>
            <!-- Customer info -->
            <transition
                enter-active-class="transition ease-out duration-100"
                enter-class="opacity-0 translate-y-1"
                enter-to-class="opacity-0 translate-y-1"
                leave-active-class="transition ease-in duration-150"
                leave-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
            >
              <div v-show="step === 'info'">
                <h2 class="text-lg font-medium text-gray-900">Contact information</h2>
                <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                  <ValidationProvider name="first name" rules="required" v-slot="{ errors }">
                    <div>
                      <label for="first-name" class="block text-sm font-medium text-gray-700">First name</label>
                      <div class="mt-1 relative">
                        <input type="text" id="first-name" name="first-name" autocomplete="given-name"
                               :class="inputClasses(errors[0])"
                               v-model="firstName"
                        >
                        <error-icon v-if="errors[0]"></error-icon>
                      </div>
                      <p class="mt-2 text-sm text-red-600" v-if="errors[0]">
                        {{ errors[0] }}
                      </p>
                    </div>
                  </ValidationProvider>
                  <ValidationProvider name="last name" rules="required" v-slot="{ errors }">
                    <div>
                      <label for="last-name" class="block text-sm font-medium text-gray-700">Last name</label>
                      <div class="mt-1 relative">
                        <input type="text" id="last-name" name="last-name" autocomplete="family-name"
                               :class="inputClasses(errors[0])"
                               v-model="lastName"
                        >
                        <error-icon v-if="errors[0]"></error-icon>
                      </div>
                      <p class="mt-2 text-sm text-red-600" v-if="errors[0]">
                        {{ errors[0] }}
                      </p>
                    </div>
                  </ValidationProvider>
                </div>
                <div class="mt-4">
                  <ValidationProvider name="email" rules="required|email" :debounce="500" v-slot="{ errors }">
                    <label for="email" class="block text-sm font-medium text-gray-700">Email address</label>
                    <div class="mt-1 relative">
                      <input type="email" id="email" name="email" autocomplete="email"
                             v-model="email"
                             :class="inputClasses(errors[0])"
                      >
                      <error-icon v-if="errors[0]"></error-icon>
                    </div>
                    <p class="mt-2 text-sm text-red-600" v-if="errors[0]">
                      {{ errors[0] }}
                    </p>
                  </ValidationProvider>
                </div>
              </div>
            </transition>
            <div v-show="step !== 'info'" @click="reloadPage" class="divide-y divide-gray-200 border-t border-b border-gray-200 py-6 cursor-pointer">
              <div>
                <h2 class="text-lg font-medium text-gray-700">Contact information</h2>
                <p class="text-sm text-gray-600 mt-2">{{ firstName}} {{ lastName }}</p>
                <p class="text-sm text-gray-600 mt-1">{{ email }}</p>
              </div>
            </div>
            <transition
                enter-active-class="transition ease-out duration-100"
                enter-class="opacity-0 translate-y-1"
                enter-to-class="opacity-0 translate-y-1"
                leave-active-class="transition ease-in duration-150"
                leave-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
            >
              <!-- Delivery method -->
              <div v-show="step === 'delivery'" class="mt-12">
                <fieldset>
                  <legend class="text-lg font-medium text-gray-900">Delivery method</legend>
                  <div class="mt-4 grid grid-cols-1 gap-y-6 sm:gap-x-4">
                    <!--
                      Checked: "border-transparent", Not Checked: "border-gray-300"
                      Active: "ring-2 ring-indigo-500"
                    -->
                    <label class="relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none">
                      <input type="radio" name="delivery-method" value="Standard" class="sr-only"
                             aria-labelledby="delivery-method-0-label"
                             aria-describedby="delivery-method-0-description-0 delivery-method-0-description-1">
                      <span class="flex flex-1">
                  <span class="flex flex-col">
                    <span id="delivery-method-0-label"
                          class="block text-sm font-medium text-gray-900">Local Pickup</span>
                    <span id="delivery-method-0-description-0" class="mt-1 flex items-center text-sm text-gray-500">Your order will be made available for pickup at a practice or team event.</span>
                    <span id="delivery-method-0-description-1"
                          class="mt-6 text-sm font-medium text-gray-900">FREE</span>
                  </span>
                </span>
                      <!-- Not Checked: "hidden" -->
                      <svg class="h-5 w-5" :style="{ color: brandColor }" viewBox="0 0 20 20" fill="currentColor"
                           aria-hidden="true">
                        <path fill-rule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                              clip-rule="evenodd"/>
                      </svg>
                      <!--
                        Active: "border", Not Active: "border-2"
                        Checked: "border-indigo-500", Not Checked: "border-transparent"
                      -->
                      <span class="pointer-events-none absolute -inset-px rounded-lg border-2" aria-hidden="true"></span>
                    </label>
                  </div>
                </fieldset>
              </div>
            </transition>
            <div v-show="step !== 'info' && step !== 'delivery'" @click="step='delivery'" class="divide-y divide-gray-200 border-b border-gray-200 py-6 cursor-pointer">
              <div>
                <h2 class="text-lg font-medium text-gray-700">Delivery method</h2>
                <p class="text-sm text-gray-600 mt-2">Local pickup</p>
              </div>
            </div>
            <transition
                enter-active-class="transition ease-out duration-100"
                enter-class="opacity-0 translate-y-1"
                enter-to-class="opacity-0 translate-y-1"
                leave-active-class="transition ease-in duration-150"
                leave-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
            >
              <!-- Payment -->
              <div v-if="step === 'billing'" class="mt-12">
                <h2 class="text-lg font-medium text-gray-900">Billing information</h2>
                <div v-if="useStripe" id="payment-element">
                  <!--Stripe.js injects the Payment Element-->
                </div>
                <div v-else-if="useJustifi">
                  <div class="flex mt-2 flex-col space-y-4">
                    <ValidationProvider name="name on card" rules="required" v-slot="{ errors }">
                      <div>
                        <label for="name_on_card" class="block text-sm font-medium text-gray-700">Name on card</label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                          <input type="text"
                                 name="name on card"
                                 id="name_on_card"
                                 v-model="nameOnCard"
                                 :class="inputClasses(errors[0])">
                          <error-icon v-if="errors[0]"></error-icon>
                        </div>
                        <p class="mt-2 text-sm text-red-600" v-if="errors[0]">
                          {{ errors[0] }}
                        </p>
                      </div>
                    </ValidationProvider>
                    <ValidationProvider name="zip" rules="required" v-slot="{ errors }">
                      <div>
                        <label for="zip" class="block text-sm font-medium text-gray-700">ZIP</label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                          <input type="text"
                                 name="zip"
                                 id="zip"
                                 v-model="zip"
                                 :class="inputClasses(errors[0])">
                          <error-icon v-if="errors[0]"></error-icon>
                        </div>
                        <p class="mt-2 text-sm text-red-600" v-if="errors[0]">
                          {{ errors[0] }}
                        </p>
                      </div>
                    </ValidationProvider>
                    <template v-if="justifiLoading">
                      <wrestler-icon :stroke-color="brandTextColor" class="mx-auto"></wrestler-icon>
                    </template>
                    <justifi-card-form @cardFormReady="justifiLoading = false" validation-mode="onBlur"></justifi-card-form>
                  </div>
                </div>
              </div>
            </transition>
            <div class="mt-6">
              <recaptcha
                  ref="recaptcha"
                  :captcha-action="captchaAction"
                  :captcha-site-key="captchaSiteKey"
                  :captcha-v3-site-key="captchaV3SiteKey"
                  style="margin-bottom: 16px; margin-top: 16px;"
              ></recaptcha>
              <button type="submit"
                      v-if="step !== 'billing'"
                      @click.prevent="handleSubmit(finishStep)"
                      :disabled="creatingCustomer || fetchingPreview || creatingToken || creatingOrder"
                      :style="{ color: brandTextColor, backgroundColor: brandColor}"
                      class="flex justify-center space-x-4  relative w-full rounded-md border border-transparent px-4 py-3 text-base font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-50"
              >
                <span>Next</span>
                <span v-if="creatingCustomer || fetchingPreview || creatingToken || creatingOrder">
                <wrestler-icon :stroke-color="brandTextColor"></wrestler-icon>
              </span>
              </button>
              <p v-if="error" class="text-red-500 text-xs text-center py-2">{{ errorMessage }}</p>
              <div class="text-center text-gray-500 text-xs">
                <p v-if="step === 'info'" class="mt-1">
                  Next step: Pick a delivery method
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-3 w-3 text-gray-500 inline">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                  </svg>
                </p>
                <p v-if="step === 'delivery'" class="mt-1">
                  Next step: Enter billing information
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-3 w-3 text-gray-500 inline">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                  </svg>
                </p>
              </div>
            </div>
          </div>
          <!-- Order summary -->
          <div class="mt-10 lg:mt-0">
            <h2 class="text-lg font-medium text-gray-900">Order summary</h2>
            <div class="mt-4 rounded-lg border border-gray-200 bg-white shadow-sm">
              <h3 class="sr-only">Items in your cart</h3>
              <ul role="list" class="divide-y divide-gray-200 px-4 sm:px-6">
                <!-- We use rails UJS to simplify removing items and to confuse all developers -->
                <slot name="items"></slot>
              </ul>
              <div class="border-t border-gray-200 px-4 py-6 sm:px-6">
                <div v-if="!appliedScholarshipCode">
                  <div class="flex items-center">
                    <input
                      type="text"
                      v-model="scholarshipCode"
                      placeholder="Enter scholarship code"
                      :disabled="fetchingPreview"
                      class="shadow-sm block w-full border-gray-300 rounded-md sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      @click="previewOrder"
                      :disabled="fetchingPreview"
                      :style="{ color: brandTextColor, backgroundColor: brandColor }"
                      class="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                      :class="{ 'opacity-50 cursor-not-allowed': fetchingPreview }"
                    >
                      {{ fetchingPreview && scholarshipCode ? 'Applying...' : 'Apply' }}
                    </button>
                  </div>
                  <p v-if="error && errorMessage.includes('Invalid scholarship code')" class="text-red-500 text-xs text-center py-2">{{ errorMessage }}</p>
                </div>
                <div v-else>
                  <div class="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                    <div>
                      <span class="text-sm font-medium">Applied scholarship code:</span>
                      <span class="text-sm ml-2 text-green-600">{{ scholarshipCode }}</span>
                    </div>
                    <button
                      @click="removeScholarshipCode"
                      class="text-sm text-gray-600 hover:text-gray-800 focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                          </svg>
                    </button>
                  </div>
                  <div class="flex items-center justify-between mt-2">
                    <dt class="text-sm">Discount</dt>
                    <dd class="text-sm font-medium text-gray-900">{{ formatAsCurrency(discountInvoiceItem.amount, 2) }}</dd>
                  </div>
                </div>
              </div>
              <dl class="space-y-6 border-t border-gray-200 px-4 py-6 sm:px-6" v-if="!fetchingPreview">
                <div class="flex items-center justify-between">
                  <dt class="text-sm">Subtotal</dt>
                  <dd class="text-sm font-medium text-gray-900">{{ formatAsCurrency(subtotal, 2) }}</dd>
                </div>
                <div class="flex items-center justify-between" v-if="wiqFeeTotal > 0">
                  <dt class="text-sm">WrestlingIQ Service Fee</dt>
                  <dd class="text-sm font-medium text-gray-900">{{ formatAsCurrency(wiqFeeTotal, 2) }}</dd>
                </div>
                <div class="" v-if="total > 0 && feeCoverageTotal > 0">
                  <div class="bg-gray-50 rounded-md py-2 px-4 mb-4 flex justify-between gap-x-4 border"
                       :style="{ borderColor: brandColor}">
                    <button @click="feeAddedToCart = !feeAddedToCart" type="button"
                            :class="feeAddedToCart ? 'bg-teal-500' : 'bg-gray-200'"
                            class="self-center relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                            role="switch"
                            :aria-checked="feeAddedToCart">
                      <span class="sr-only">Add fee to cart to cover fees</span>
                      <!-- Enabled: "translate-x-5", Not Enabled: "translate-x-0" -->
                      <span aria-hidden="true" :class="feeAddedToCart ? 'translate-x-5' : 'translate-x-0'"
                            class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition ease-in-out duration-200">
                </span>
                    </button>
                    <p class="text-xs">
                      <template v-if="feeAddedToCart">
                  <span class="font-bold text-teal-600">{{ formatAsCurrency(feeCoverageTotal, 2) }} has been
                  added to cover processing fees</span><br/> Thank you! Your extra contribution means {{ teamName }}
                        will receive
                        100% of your <span class="font-medium" :style="{ color: brandColor }">{{
                          formatAsCurrency(subtotal, 2)
                        }}</span> order.
                      </template>
                      <template v-else>
                      <span class="font-bold text-gray-900">Add {{
                          formatAsCurrency(feeCoverageTotal, 2)
                        }} to cover fees?</span><br/>
                        Round up to pay for card processing fees and ensure {{ teamName }} receives 100% of your order
                        revenue.
                      </template>
                    </p>
                  </div>
                </div>
                <div class="flex items-center justify-between border-t border-gray-200 pt-6">
                  <dt class="text-base font-medium">Total</dt>
                  <dd class="text-base font-medium text-gray-900">{{ formatAsCurrency(total, 2) }}</dd>
                </div>
              </dl>
              <div class="border-t border-gray-200 px-4 py-6 sm:px-6" v-if="step === 'billing'">
                <button type="submit"
                        :style="{ color: brandTextColor, backgroundColor: brandColor}"
                        :disabled="creatingCustomer || fetchingPreview || creatingToken || creatingOrder"
                        @click.prevent="handleSubmit(finishStep)"
                        class="flex justify-center space-x-4 relative w-full rounded-md border border-transparent px-4 py-3 text-base font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                >
                  <span>Place your order</span>
                  <span v-if="creatingCustomer || fetchingPreview || creatingToken || creatingOrder">
                      <wrestler-icon :stroke-color="brandTextColor"></wrestler-icon>
                  </span>
                </button>
                <p v-if="error" class="text-red-500 text-xs text-center py-2">{{ errorMessage }}</p>
                <div class="text-center text-gray-500 text-xs">
                  <p class="mt-1">
                    <svg class="h-3 w-3 text-gray-500 inline" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clip-rule="evenodd"/>
                    </svg>
                    Secure payment processing provided by
                    <span v-if="useStripe">Stripe</span><span v-else>WrestlingIQ</span><br/>
                  </p>
                  <p class="mt-4">
                    <a target="_blank"
                       href="https://www.wrestlingiq.com?ref=onlinestore">Powered by <span
                        class="underline text-emerald-500">WrestlingIQ</span></a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </div>
  </div>
</template>
<script>
import { currencyMix } from '../shared/currency_mix';
import WrestlerIcon from './wrestler-icon.vue';
import ErrorIcon from './error-icon.vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { recaptchaMix } from '../shared/recaptcha_mix.js';
import Recaptcha from '../shared/recaptcha.vue';
import { billingPartnerMix } from '../shared/billing_partner_mix';
import { errorableMix } from '../wrestling/vue/mixins/errorable_mix';

export default {
  name: 'checkout',
  components: {
    ErrorIcon,
    Recaptcha,
    ValidationProvider,
    ValidationObserver,
    WrestlerIcon
  },
  mixins: [currencyMix, recaptchaMix, billingPartnerMix, errorableMix],
  props: {
    brandColor: {
      type: String,
      default: '#27AA83',
    },
    brandTextColor: {
      type: String,
      default: '#FFF'
    },
    store: {
      type: Object,
      required: true
    },
    cart: {
      type: Object,
      required: true,
    },
    teamName: {
      type: String,
      required: true,
    },
    teamSlug: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',

      feeAddedToCart: false,
      invoicePreview: null,
      fetchingPreview: false,

      cancelSource: null,
      error: false,
      errorMessage: false,

      creatingOrder: false,
      creatingCustomer: false,
      customerId: null,
      paymentMethodToken: null,
      metadata: {
        // TODO metadata for personalization stuff
      },

      step: 'info', // info, deliery, payment
      stripeIntent: null,

      scholarshipCode: '',
      appliedScholarshipCode: false,
      discountInvoiceItem: null,
    };
  },
  computed: {
    // TODO DRY or move this over into contribution and donor box too
    wiqFeeTotal() {
      if (!this.invoicePreview) {
        return 0;
      }

      // Only the wiq portion
      return _.sum(_.map(this.invoicePreview.invoice_items, (ii) => {
        if (this.invoiceItemIsWIQFee(ii)) {
          return ii.amount;
        }

        return 0;
      }));
    },
    feeCoverageTotal() {
      if (!this.invoicePreview) {
        return 0;
      }

      return _.sum(_.map(this.invoicePreview.invoice_items, (ii) => {
        if (ii.description === 'Contribution to cover card processing and WIQ fees') {
          return ii.amount;
        }

        return 0;
      }));
    },
    subtotal() {
      if (!this.invoicePreview) {
        return 0;
      }

      return this.invoicePreview.amount_due - this.wiqFeeTotal - this.feeCoverageTotal;
    },
    total() {
      if (this.invoicePreview) {
        let total = this.invoicePreview.amount_due;
        if (total === 0) {
          return 0;
        }

        if (!this.feeAddedToCart) {
          // Rememer that the preview endpoint always returns with fee coverage added, so we need to subtract that if the user does not have it selected
          total = total - this.feeCoverageTotal;
        }

        return total;
      }

      return 0;
    },
  },
  mounted() {
    this.previewOrder();
  },
  methods: {
    reloadPage() {
      window.location.reload()
    },
    invoiceItemIsWIQFee(ii) {
      return ii.transactable_type === 'ApplicationFee';
    },
    inputClasses(error) {
      const normalClasses = 'shadow-sm block w-full border-gray-300 rounded-md sm:text-sm';
      if (!error) {
        return normalClasses;
      }

      return `border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:ring-red ${normalClasses}`;
    },
    finishStep() {
      if (this.step === 'info') {
        this.nameOnCard = `${this.firstName} ${this.lastName}`
        this.createCustomer();
      } else if (this.step === 'delivery') {
        this.step = 'billing'
        let vm = this;
        vm.$forceNextTick(() => {
          vm.setupPaymentElement(vm.stripeIntent, vm.brandColor, "#374151", true);
        });
      } else if (this.step === 'billing') {
        this.getPaymentToken(this.nameOnCard, this.email, this.zip);
      }
    },
    removeScholarshipCode() {
      this.appliedScholarshipCode = false;
      this.discountInvoiceItem = null;
      this.scholarshipCode = '';
      this.previewOrder();
    },
    // We allow fee coverage to be passed in, because the preview endpoint
    // always uses it to calc fee to be added.
    params(customerId, feeCoverage, token) {
      const items = _.map(this.cart.items, (t) => {
        let itemUpsells = []
        t.upsells.forEach((u) => {
          itemUpsells.push({
            id: u.upsell.id,
            user_input: u.user_input,
          })
        });
        return {
          id: t.plan.id,
          type: 'BillingPlan',
          quantity: t.quantity,
          interval: 'once',
          upsells: itemUpsells,
        };
      });

      return {
        order: {
          customer_id: customerId,
          fee_coverage_added: feeCoverage,
          payment_method_token: token,
          items: items,
          metadata: this.metadata,
          coupon_code: this.scholarshipCode,
        }
      };
    },
    previewOrder() {
      const vm = this;
      const url = vm.$apiService.previewOrderUrl(this.store.slug);
      // Cancel inflight requests
      if (vm.cancelSource) {
        vm.cancelSource.cancel();
      }
      vm.cancelSource = axios.CancelToken.source();
      vm.fetchingPreview = true;
      axios.post(url, vm.params(null, true, null), { cancelToken: vm.cancelSource.token })
          .then((response) => {
            vm.invoicePreview = response.data;
            vm.cancelSource = null;
            vm.fetchingPreview = false;
            // Check if the invoice preview contains a valid scholarship code
            const scholarshipItem = vm.invoicePreview.invoice_items.find(item =>
              item.transactable_type === 'Coupon' &&
              item.amount < 0 &&
              item.transactable &&
              item.transactable.code === vm.scholarshipCode
            );

            let incorrectlyEnteredCode = false;

            if (scholarshipItem) {
              vm.appliedScholarshipCode = true;
              vm.discountInvoiceItem = scholarshipItem;
            } else {
              vm.appliedScholarshipCode = false;
              // If scholarship code was entered but not found in the invoice preview, denote that it failed, but still clear it.
              if (vm.scholarshipCode !== '') {
                incorrectlyEnteredCode = true;
              }
              // If no valid scholarship found, clear the scholarship code
              vm.scholarshipCode = '';
            }

            // If scholarship not applied successfully, show an error message
            if (!vm.appliedScholarshipCode && incorrectlyEnteredCode) {
              vm.errorMessage = 'Invalid scholarship code, please try again. Codes are case sensitive.';
              vm.error = true;
            } else {
              vm.error = false;
              vm.errorMessage = '';
            }
          })
          .catch((error) => {
            if (axios.isCancel(error)) {
              console.log('request cancelled');
            } else {
              vm.errorMessage = `Error retrieving order preview`;
              vm.error = true;
              vm.fetchingPreview = false;
            }
          });
    },
    tokenized(token) {
      this.paymentMethodToken = token;
      this.createOrder();
    },
    createOrder() {
      const vm = this;
      const url = vm.$apiService.orderUrl(this.store.slug);
      vm.creatingOrder = true;
      axios.post(url, vm.params(vm.customerId, vm.feeAddedToCart, vm.paymentMethodToken))
          .then((response) => {
            vm.creatingOrder = false;
            const chargeToken = response.data.charges[0].billing_partner_id;
            const url = `/${vm.teamSlug}/store/${this.store.slug}/order/${chargeToken}`;
            window.location.assign(url);
          })
          .catch((error) => {
            vm.errorMessage = vm.parseErrorResponse(error);
            vm.error = true;
            vm.creatingOrder = false;
          });
    },
    async createCustomer() {
      const vm = this;

      if (vm.creatingCustomer === true) {
        return;
      }

      vm.creatingCustomer = true;
      vm.errorMessage = '';
      vm.error = false;

      // Step 1: Create a customer
      const customerParams = {
        'g-recaptcha-response-data': await vm.$refs.recaptcha.getCaptchaResponse(),
        customer: {
          email: vm.email,
          first_name: vm.firstName,
          last_name: vm.lastName,
        },
      };
      const url = vm.$apiService.customerUrl();
      axios.post(url, customerParams)
          .then((response) => {
            vm.creatingCustomer = false;
            // Move to step 2 delivery
            vm.step = 'delivery';
            // Now actually charge the card
            vm.customerId = response.data.id;
            vm.stripeIntent = response.data.setup_intent.client_secret;
            vm.$refs.recaptcha.forceHideCaptcha();
          })
          .catch((error) => {
            vm.creatingCustomer = false;
            if (vm.isRecaptchaError(error)) {
              vm.$refs.recaptcha.handleCaptchaFailed();
              vm.errorMessage = 'Recaptcha failed. Please solve and try again.';
            } else {
              vm.errorMessage = `Error, please try again later or contact support@wrestlingiq.com ${error.toString()}`;
            }
            vm.error = true;
          });
    },
  }
};
</script>
<style scoped>
</style>
